/**
 * Daily 시드 — `daily_table` 과 `daily_row` 모양 그대로.
 *
 * 9월 1일부터 오늘(TODAY) 전날까지, Daily 를 쓰는 사람의 근무일마다 하루 한 행을 만든다. 사람이
 * 일일이 적기엔 많아 규칙으로 만들되 실제처럼 보이도록 엔티티를 따른다:
 *  - 그날 배치된(TEAMS) 프로젝트 중 진행 중인 단계가 있는 곳에서만 일한다(PM 인 곳에 더 많이)
 *  - 한 일 문구는 그날 그 프로젝트의 단계(data/projects.ts PHASE_PLANS)와 다가오는 마일스톤에서 온다
 *  - 줄의 계획 칸은 전 근무일 저녁에 써 둔 것이다(plan_saved_at)
 *  - 승인된 연차로 쉰 시간만큼 한 일이 줄고, 하루 연차면 하루가 아예 없다
 *  - 휴직, 퇴사한 사람은 마지막 근무일(LAST_WORKDAY) 뒤로 없다
 *
 * 「오늘」은 SEED_TODAY 와 실제 오늘 중 늦은 날이다(TODAY) — 시드가 끝난 뒤에 열어도 오늘 보드가
 * 비지 않게 그 사이 근무일도 지나간 하루로 채운다. 오늘은 사람마다 미제출·계획만·일부 완료·제출이
 * 섞이고, 그 다음 날에는 몇 사람의 내일 할 일만 있다. 고정 난수라 같은 날 열면 같다.
 *
 * 한 사람이 한 프로젝트에 작업(줄)을 둘 올리는 날도 있다 — 보드가 작업 단위라서다.
 *
 * 정규와 연장은 줄에 넣지 않는다 — 하루 합계와 standard_hours 에서 나온다.
 */
import { shiftDate, today, weekStartOf } from "@/lib/dates";
import type { Daily, DailyCategory, DailyRow } from "@/types";
import { leaveHoursOn } from "./attendance";
import { PM_OF, TEAMS, phaseNameOn, plannedLogNear, projects } from "./projects";
import { SEED_START, SEED_TODAY, at, isWorkday, nearestWorkday, rng, workdays } from "./seed";
import { LAST_WORKDAY, staff } from "./staff";

/** mock 테이블의 한 행 — 응답에는 없는 user_id 를 들고 있다. */
export interface MockDaily extends Daily {
  user_id: string;
}

/**
 * 분류 — 설치할 때 들어오는 기본 분류(backend `CATEGORY_SEED`: 사내 업무와 그 아래 셋, 기타)에
 * 관리자가 「설계 업무」와 그 아래 넷을 더한 상태. 프로젝트 작업에도 분류를 붙인다.
 */
export const dailyCategories: DailyCategory[] = [
  { id: "cat-design", parent_id: null, name: "설계 업무", sort_order: 1 },
  { id: "cat-office", parent_id: null, name: "사내 업무", sort_order: 2 },
  { id: "cat-etc", parent_id: null, name: "기타", sort_order: 3 },
  { id: "cat-drawing", parent_id: "cat-design", name: "도면 작성", sort_order: 1 },
  { id: "cat-review", parent_id: "cat-design", name: "검토", sort_order: 2 },
  { id: "cat-meeting", parent_id: "cat-design", name: "회의·협의", sort_order: 3 },
  { id: "cat-site", parent_id: "cat-design", name: "현장", sort_order: 4 },
  { id: "cat-edu", parent_id: "cat-office", name: "교육", sort_order: 1 },
  { id: "cat-admin", parent_id: "cat-office", name: "관리", sort_order: 2 },
  { id: "cat-sales", parent_id: "cat-office", name: "영업과 제안", sort_order: 3 },
];

/** 프로젝트 작업 문구로 고르는 분류 — 맞는 말이 없으면 분류 없이 둔다. */
const CATEGORY_BY_WORD: [RegExp, string][] = [
  [/회의|협의|미팅|보고|동석/, "cat-meeting"],
  [/현장|감리|답사|사진/, "cat-site"],
  [/검토|조사|스터디|법규/, "cat-review"],
  [/도면|상세|일람표|도서|작성|정리|면적표|비교표/, "cat-drawing"],
];
const categoryOf = (body: string) =>
  CATEGORY_BY_WORD.find(([word]) => word.test(body))?.[1] ?? null;

const STANDARD_HOURS = 8;

/** 시드의 오늘 — 실제 오늘이 더 늦으면 그날. */
const TODAY = today() > SEED_TODAY ? today() : SEED_TODAY;

/** 단계 이름별 한 일 문구. 이름이 없는 단계(프로젝트마다 끼운 것)는 kickoff 를 쓴다. */
const WORK: Record<string, string[]> = {
  kickoff: ["착수 준비 자료 검토", "과업 지시서 검토", "대지 자료 수집", "착수 회의 준비"],
  기획설계: [
    "대지 현황 조사",
    "법규 검토(건폐율, 용적률, 높이 제한)",
    "매스 스터디 3안 작성",
    "건축주 요구사항 정리",
  ],
  기본설계: [
    "평면 계획 수정",
    "입면 스터디",
    "기본설계 도면 작성",
    "면적표 정리",
    "구조 방식 검토 회의",
    "발주처 보고 자료 작성",
  ],
  인허가: [
    "건축허가 도서 정리",
    "인허가 보완 요청 검토",
    "주차 계획 보완",
    "관계 부서 협의 자료 작성",
    "심의 자료 작성",
  ],
  실시설계: [
    "실시설계 상세도 작성",
    "창호 일람표 정리",
    "마감 상세 검토",
    "구조 도면 취합",
    "설비 도면 간섭 검토",
    "물량 산출 검토",
  ],
  설계변경: [
    "설계변경 도서 작성",
    "변경 내역서 정리",
    "발주처 변경 요청 반영",
    "변경 전후 비교표 작성",
  ],
  감리: [
    "현장 감리, 사진 기록",
    "시공 상세 검토 회신",
    "자재 승인 검토",
    "감리 일지 작성",
    "공정 회의 참석",
  ],
};

/** 한 일 끝에 붙는 구체적인 대상 — 같은 문구가 줄줄이 반복되지 않게. */
const DETAIL = [
  "1층 로비",
  "3층 코어",
  "지하 주차장",
  "옥상 조경",
  "외벽 마감",
  "계단실",
  "주출입구",
  "남측 입면",
  "기계실",
  "배치도",
];

/** 프로젝트에서 일하지 않는 사람의 하루 — 분류만 골라 적는다. */
const OFFICE_WORK: Record<string, { category: string; body: string[] }[]> = {
  "u-acc": [
    {
      category: "cat-admin",
      body: ["월말 결산 자료 정리", "용역비 청구서 발행", "경비 정산 검토", "4대 보험 신고"],
    },
    { category: "cat-office", body: ["사무용품 발주", "사내 행사 준비", "법인 차량 관리"] },
  ],
  변경민: [
    {
      category: "cat-admin",
      body: ["WorkThings 운영 점검", "역할 정리", "계약 서류 검토", "주간 회의 진행"],
    },
    { category: "cat-sales", body: ["제안서 검토", "신규 발주처 미팅 준비"] },
  ],
};

/** 오늘 늘 「일부 완료」인 사람 — 개발 중 로그인해 보는 계정들. */
const MIXED_TODAY = ["u-staff", "u-pm2", "변경민"];

/** 대표는 Daily 를 쓰지 않는다. 역할이 아니라 그 사람이라 id 로 가린다. */
const DAILY_WRITERS = staff.filter((u) => u.id !== "u-owner").map((u) => u.id);

/** 그날 그 사람이 소속된 진행 중 프로젝트 — PM 인 곳은 두 번 넣어 더 자주 뽑히게. */
function projectsOn(userId: string, date: string): string[] {
  return TEAMS.filter((t) => t.user_id === userId && t.from <= date && (!t.to || date <= t.to))
    .filter((t) => phaseNameOn(t.project_id, date) !== null)
    .flatMap((t) =>
      PM_OF.get(t.project_id) === userId ? [t.project_id, t.project_id] : [t.project_id]
    );
}

function workText(r: ReturnType<typeof rng>, projectId: string, date: string): string {
  const upcoming = plannedLogNear(projectId, date);
  if (upcoming && r.chance(0.5)) return `${upcoming} 준비`;
  const phrase = r.pick(WORK[phaseNameOn(projectId, date) ?? ""] ?? WORK.kickoff);
  return r.chance(0.6) ? `${phrase} — ${r.pick(DETAIL)}` : phrase;
}

/** 총시간을 줄 수만큼 0.5 단위로 나눈다 — 앞 줄일수록 크다. */
function splitHours(r: ReturnType<typeof rng>, total: number, count: number): number[] {
  if (count === 1) return [total];
  const parts: number[] = [];
  let left = total;
  for (let i = 0; i < count - 1; i++) {
    const share = Math.max(0.5, Math.round(left * (0.5 + r.next() * 0.3) * 2) / 2);
    const h = Math.min(share, left - 0.5 * (count - 1 - i));
    parts.push(h);
    left -= h;
  }
  parts.push(left);
  return parts;
}

interface Target {
  project_id: string | null;
  category_id: string | null;
  body: string;
}

function targetsFor(r: ReturnType<typeof rng>, userId: string, date: string): Target[] {
  const office = OFFICE_WORK[userId];
  if (office) {
    const rows: Target[] = office
      .filter((_, i) => i === 0 || r.chance(0.4))
      .map((o) => ({ project_id: null, category_id: o.category, body: r.pick(o.body) }));
    // 프로젝트에 이름만 올린 관리자도 가끔은 그 프로젝트 일을 한다.
    const pool = projectsOn(userId, date);
    if (pool.length && r.chance(0.2))
      rows.push({ project_id: pool[0], category_id: null, body: "발주처 협의 동석" });
    return rows;
  }
  const pool = projectsOn(userId, date);
  if (!pool.length)
    return [{ project_id: null, category_id: "cat-office", body: "사내 업무 지원" }];
  const want = r.chance(0.4) ? 1 : r.chance(0.75) ? 2 : 3;
  const chosen: string[] = [];
  for (let i = 0; i < 10 && chosen.length < want; i++) {
    const p = r.pick(pool);
    if (!chosen.includes(p)) chosen.push(p);
  }
  const rows: Target[] = chosen.flatMap((p) => {
    const body = workText(r, p, date);
    // 열에 한 번쯤은 분류 없이 올린 작업도 섞인다.
    const first = { project_id: p, category_id: r.chance(0.85) ? categoryOf(body) : null, body };
    // 하루에 프로젝트마다 한 줄이다. 한 프로젝트에서 작업을 둘 한 날은 한 줄에 두 줄로 적는다 —
    // 고른 프로젝트가 적을수록 잦다.
    if (!r.chance(chosen.length === 1 ? 0.6 : 0.3)) return [first];
    const second = workText(r, p, date);
    return second === first.body ? [first] : [{ ...first, body: `${body}\n${second}` }];
  });
  // 인턴은 가끔 교육을 받는다.
  if (userId === "u-st5" && r.chance(0.15))
    rows.push({ project_id: null, category_id: "cat-edu", body: "신입 BIM 교육" });
  return rows;
}

/**
 * 오늘 · 내일의 줄 — 비프로젝트 업무는 Daily 화면에서 「데일리 추가」로 직접 더해야 생긴다. 프로젝트 일이
 * 있는 사람의 오늘 · 내일에는 미리 넣어 두지 않는다. 사내 일만 하는 사람(관리자)은 그대로 둔다.
 */
const upcoming = (targets: Target[]) =>
  targets.some((t) => t.project_id) ? targets.filter((t) => t.project_id) : targets;

/** 계획의 예상 시간 — 하루 8시간을 줄 수만큼 0.5 단위로 나눈 값. */
const estimate = (count: number) => Math.max(0.5, Math.round((STANDARD_HOURS / count) * 2) / 2);

const row = (
  dailyId: string,
  position: number,
  target: Target,
  fields: Partial<DailyRow>
): DailyRow => ({
  id: `${dailyId}-r${position}`,
  position,
  project_id: target.project_id,
  project_name: projects.find((p) => p.id === target.project_id)?.name ?? null,
  category_id: target.category_id,
  category_name: dailyCategories.find((c) => c.id === target.category_id)?.name ?? null,
  plan_description: "",
  plan_hours: 0,
  plan_saved_at: null,
  plan_updated_at: null,
  description: "",
  actual_hours: 0,
  completed_at: null,
  ...fields,
});

const header = (userId: string, date: string, rows: DailyRow[], submittedAt: string | null) => {
  const total = rows.reduce((sum, r) => sum + r.actual_hours, 0);
  return {
    id: `daily-${userId}-${date}`,
    user_id: userId,
    work_date: date,
    week_start: weekStartOf(date),
    status: submittedAt ? ("submitted" as const) : ("draft" as const),
    submitted_at: submittedAt,
    standard_hours: STANDARD_HOURS,
    total_hours: total,
    regular_hours: Math.min(total, STANDARD_HOURS),
    overtime_hours: Math.max(0, total - STANDARD_HOURS),
    rows,
    notes: [],
    note: "",
  };
};

/** 지나간 하루 — 계획을 전날 저녁에 쓰고 그날 저녁에 제출했다. */
function pastDay(userId: string, date: string): MockDaily | null {
  const r = rng(`${userId}:${date}`);
  const leave = leaveHoursOn(userId, date);
  if (leave >= 8) return null;
  const targets = targetsFor(r, userId, date);
  const id = `daily-${userId}-${date}`;
  const planSavedAt = at(nearestWorkday(date, -1), "18:25");

  // 하루 3% 는 제출을 빠뜨린다 — 미제출이 화면에서 어떻게 보이는지.
  const missed = r.chance(0.03);
  const available = STANDARD_HOURS - leave;
  const nearDeadline = targets.some((t) => t.project_id && plannedLogNear(t.project_id, date));
  const overtime = nearDeadline && r.chance(0.6) ? 1 + r.int(3) : r.chance(0.12) ? 1 : 0;
  const hours = splitHours(r, available + overtime, targets.length);
  const rows = targets.map((target, i) => {
    // 줄의 대부분은 전날 계획한 것이다. 계획 없이 하게 된 일도 있다.
    const planned = r.chance(0.75);
    return row(id, i, target, {
      plan_description: planned ? target.body : "",
      plan_hours: planned ? estimate(targets.length) : 0,
      plan_saved_at: planned ? planSavedAt : null,
      description: missed ? "" : target.body,
      actual_hours: missed ? 0 : hours[i],
      // 작업마다 끝낸 때 제출한다 — 오후에 하나씩.
      completed_at: missed
        ? null
        : at(date, `${14 + Math.min(i, 4)}:${String(10 + r.int(45)).padStart(2, "0")}`),
    });
  });
  const submittedAt = missed ? null : at(date, `18:${String(30 + r.int(29)).padStart(2, "0")}`);
  return header(userId, date, rows, submittedAt);
}

/**
 * 오늘 — 사람마다 단계가 다르다. 미제출(아무것도 없음), 계획만, 일부 완료, 모두 완료(제출).
 * 계획은 오늘 아침에 올렸고, 완료한 작업은 오후에 한 일과 시간을 붙였다.
 */
function todayDay(userId: string): MockDaily | null {
  const r = rng(`${userId}:${TODAY}:plan`);
  const roll = r.next();
  // 로그인해 볼 법한 사람은 늘 「일부 완료」 — 내 작업에 제출한 것과 아직 안 한 것이 섞이게.
  const stage = MIXED_TODAY.includes(userId)
    ? "partly"
    : roll < 0.15
      ? "none"
      : roll < 0.55
        ? "plan"
        : roll < 0.85
          ? "partly"
          : "done";
  if (stage === "none" || !isWorkday(TODAY) || leaveHoursOn(userId, TODAY) >= 8) return null;
  const targets = upcoming(targetsFor(r, userId, TODAY));
  const id = `daily-${userId}-${TODAY}`;
  const planSavedAt = at(TODAY, `09:${String(r.int(50)).padStart(2, "0")}`);
  const hours = splitHours(r, STANDARD_HOURS, targets.length);
  const rows = targets.map((target, i) => {
    // 일부 완료: 앞에서부터 번갈아 끝냈고, 마지막 작업은 늘 남아 있다.
    const done = stage === "done" || (stage === "partly" && i % 2 === 0 && i < targets.length - 1);
    return row(id, i, target, {
      plan_description: target.body,
      plan_hours: estimate(targets.length),
      plan_saved_at: planSavedAt,
      description: done ? target.body : "",
      actual_hours: done ? hours[i] : 0,
      completed_at: done
        ? at(TODAY, `${13 + Math.min(i, 4)}:${String(10 + r.int(45)).padStart(2, "0")}`)
        : null,
    });
  });
  return header(userId, TODAY, rows, stage === "done" ? at(TODAY, "17:40") : null);
}

/** 내일 — 할 일만 미리 써 둔 하루. 계획 칸만 차 있다. */
function tomorrowDay(userId: string): MockDaily {
  const tomorrow = shiftDate(TODAY, 1);
  const r = rng(`${userId}:${tomorrow}:plan`);
  const targets = upcoming(targetsFor(r, userId, tomorrow));
  const id = `daily-${userId}-${tomorrow}`;
  const rows = targets.map((target, i) =>
    row(id, i, target, {
      plan_description: target.body,
      plan_hours: estimate(targets.length),
      plan_saved_at: at(TODAY, "18:10"),
    })
  );
  return header(userId, tomorrow, rows, null);
}

/** 내일 할 일까지 써 둔 사람 — 전원이 아니라 몇 사람만. */
const PLANS_AHEAD = ["u-staff", "u-pm", "변경민"];

export const dailies: MockDaily[] = DAILY_WRITERS.flatMap((userId) => {
  const last = LAST_WORKDAY[userId] ?? shiftDate(TODAY, -1);
  const past = workdays(SEED_START, last)
    .map((date) => pastDay(userId, date))
    .filter((daily): daily is MockDaily => daily !== null);
  if (LAST_WORKDAY[userId]) return past;
  const now = todayDay(userId);
  return [
    ...past,
    ...(now ? [now] : []),
    ...(PLANS_AHEAD.includes(userId) ? [tomorrowDay(userId)] : []),
  ];
});
